import { Field, Formik, FormikErrors } from 'formik';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FormikUtils } from '../../../shared/utils';
import { CompanySubscription } from '../../../shared/models';
import { useApi } from '../../../shared/context';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import React from 'react';
import {
	CerebroDatePicker,
	CerebroDrawer,
	CerebroFileDragAndDrop,
	CerebroForm,
	CerebroHeading,
	CerebroInput,
	CerebroLabel,
	CerebroPrimaryButton,
	CerebroText,
} from '../../../shared/components';
import { Grid } from '@mui/material';
import { CerebroApiEndpoints } from '../../../shared/utils/api';
import { QueryKeys } from '../../../shared/hooks';
import { getApiResponseData } from '../../../shared/utils/axios';
import { validateDateWithinPlusMinus100Years } from '../../../shared/utils/date';

interface UploadSubscriptionAgreementFormProps {
	dealGuid: string | undefined;
	companyGuid: string;
	subscription: CompanySubscription | null;
	onClose: () => void;
}

export const UploadSubscriptionAgreement: React.FC<UploadSubscriptionAgreementFormProps> = ({
	dealGuid,
	companyGuid,
	subscription,
	onClose,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();
	const ref = React.useRef<HTMLDivElement>(null);

	const uploadSubscriptionAgreementMutation = useMutation(
		async ({ values }: UploadSubscriptionAgreementFormMutationVariables) => {
			const uploads = values.uploads;

			let formData = new FormData();
			if (values.signedOn) {
				const parsedDate = new Date(values.signedOn);
				formData.append('signedOn', parsedDate.toISOString().split('T')[0]);
			}
			formData.append('companyName', values.companyName);
			formData.append('signedBy', values.signedBy);

			if (uploads && uploads.length > 0) {
				uploads.forEach(upload => {
					formData.append('uploads', upload, upload.name);
				});
			}

			const { data, error } = await getApiResponseData<CompanySubscription>(
				api.post(
					CerebroApiEndpoints.Company.UploadSubscriptionAgreement(companyGuid, subscription?.guid),
					formData,
					{
						headers: { 'Content-Type': 'multipart/form-data' },
					}
				)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				client.invalidateQueries(QueryKeys.Deal.Subscriptions(dealGuid));
				client.invalidateQueries(QueryKeys.Deal.UpdateStatus(dealGuid));
				snackbar.enqueueSnackbar('Subscription agreement successfully uploaded.', { variant: 'success' });
				onClose();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
		}
	);

	return (
		<CerebroDrawer ref={ref} open={subscription != null} onClose={onClose}>
			<CerebroHeading>Upload Subscription Agreement</CerebroHeading>

			<Formik
				initialValues={{
					companyName: subscription?.companyName ?? '',
					signedBy: '',
					signedOn: new Date(),
					uploads: [] as File[],
				}}
				validate={validateForm}
				onSubmit={FormikUtils.MutateOnSubmit(uploadSubscriptionAgreementMutation.mutate)}
			>
				{({ values, isSubmitting, errors, touched, handleSubmit }) => (
					<CerebroForm encType="multipart/form-data" onSubmit={handleSubmit}>
						<Grid
							container
							spacing={3}
							sx={{ marginTop: 3 }}
							justifyContent={{ xs: 'center', md: 'flex-start' }}
						>
							<Grid item xs={12}>
								<CerebroText sx={{ marginBottom: 3 }}>
									<FontAwesomeIcon icon={faInfoCircle} size="sm" /> &nbsp; Once the agreement has been
									uploaded pricing can not be changed.
								</CerebroText>
							</Grid>

							<Grid item xs={11}>
								<CerebroLabel required id="uploads">
									Signed Agreement
								</CerebroLabel>
								<Field
									as={CerebroFileDragAndDrop}
									name="uploads"
									onlyPdf={true}
									multiple={false}
									sx={{ marginY: 3, width: '100%' }}
									error={FormikUtils.IsInvalid('uploads', errors, touched)}
								/>
								{FormikUtils.IsInvalid('uploads', errors, touched) && (
									<CerebroText variant="error">{errors.uploads}</CerebroText>
								)}
							</Grid>

							<Grid item xs={11}>
								<CerebroLabel id="signedBy">Company Name</CerebroLabel>
								<Field
									id="companyName"
									name="companyName"
									as={CerebroInput}
									disabled={isSubmitting}
									error={FormikUtils.IsInvalid('companyName', errors, touched)}
								/>
								{FormikUtils.IsInvalid('companyName', errors, touched) && (
									<CerebroText variant="error">{errors.companyName}</CerebroText>
								)}
							</Grid>

							<Grid item xs={11}>
								<CerebroLabel id="signedBy">Signed By</CerebroLabel>
								<Field
									id="signedBy"
									name="signedBy"
									as={CerebroInput}
									disabled={isSubmitting}
									error={FormikUtils.IsInvalid('signedBy', errors, touched)}
								/>
								{FormikUtils.IsInvalid('signedBy', errors, touched) && (
									<CerebroText variant="error">{errors.signedBy}</CerebroText>
								)}
							</Grid>

							<Grid item xs={11}>
								<CerebroLabel id="signedOn">
									Enter the date of the last signature on the agreement
								</CerebroLabel>
								<Field
									id="signedOn"
									name="signedOn"
									component={CerebroDatePicker}
									disabled={isSubmitting}
									error={FormikUtils.IsInvalid('signedOn', errors, touched)}
								/>
								{FormikUtils.IsInvalid('signedOn', errors, touched) && (
									<CerebroText variant="error">{errors.signedOn}</CerebroText>
								)}
							</Grid>
						</Grid>

						<CerebroPrimaryButton type="submit" loading={isSubmitting} sx={{ marginTop: 6 }}>
							Save
						</CerebroPrimaryButton>
					</CerebroForm>
				)}
			</Formik>
		</CerebroDrawer>
	);
};

var validateForm = (values: UploadSubscriptionAgreementFormValues): UploadSubscriptionAgreementFormErrors => {
	const errors: UploadSubscriptionAgreementFormErrors = {};

	if (!values.uploads || values.uploads.length == 0) {
		errors.uploads = 'Must select the company subscription agreement file.';
	}

	if (!values.companyName) {
		errors.companyName = 'Must enter the name of the company associated with the agreement.';
	}

	if (!values.signedBy) {
		errors.signedBy = 'Must enter who signed the agreement.';
	}

	if (!values.signedOn) {
		errors.signedOn = 'Must enter date agreement was signed.';
	}

	const signedOnError = validateDateWithinPlusMinus100Years(values.signedOn ?? null, 'Signed On');
	if (signedOnError) {
		errors.signedOn = signedOnError;
	}

	return errors;
};

type UploadSubscriptionAgreementFormErrors = FormikErrors<UploadSubscriptionAgreementFormValues>;
type UploadSubscriptionAgreementFormMutationVariables =
	FormikUtils.MutateOnSubmitVariables<UploadSubscriptionAgreementFormValues>;
interface UploadSubscriptionAgreementFormValues {
	uploads: File[];
	signedBy: string;
	signedOn: Date;
	companyName: string;
}
